import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

import { MeiQuickStats } from "@/components/tools/MeiQuickStats";
import { tools } from "@/lib/tools";
import { lazy, Suspense } from "react";

// Lazy-loaded Tool Components
const CalculadoraDAS = lazy(() => import("@/components/tools/CalculadoraDAS").then(m => ({ default: m.CalculadoraDAS })));
const LimiteFaturamento = lazy(() => import("@/components/tools/LimiteFaturamento").then(m => ({ default: m.LimiteFaturamento })));
const Precificacao = lazy(() => import("@/components/tools/Precificacao").then(m => ({ default: m.Precificacao })));
const ValidadorCNPJ = lazy(() => import("@/components/tools/ValidadorCNPJ").then(m => ({ default: m.ValidadorCNPJ })));
const SimuladorEmprestimoMEI = lazy(() => import("@/components/tools/SimuladorEmprestimoMEI").then(m => ({ default: m.SimuladorEmprestimoMEI })));
const SimuladorMaquininha = lazy(() => import("@/components/tools/SimuladorMaquininha").then(m => ({ default: m.SimuladorMaquininha })));
const SimuladorMeiVsClt = lazy(() => import("@/components/tools/SimuladorMeiVsClt").then(m => ({ default: m.SimuladorMeiVsClt })));
const IaCnaeMei = lazy(() => import("@/components/tools/IaCnaeMei").then(m => ({ default: m.IaCnaeMei })));
const CalculadoraMultaDAS = lazy(() => import("@/components/tools/CalculadoraMultaDAS").then(m => ({ default: m.CalculadoraMultaDAS })));

// Lazy-loaded MoreTools Components
const MultaDAS = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.MultaDAS })));
const SimuladorIRPF = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SimuladorIRPF })));
const SimuladorDesenquadramento = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SimuladorDesenquadramento })));
const ValorDaHora = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ValorDaHora })));
const MargemMarkup = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.MargemMarkup })));
const CalcDesconto = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalcDesconto })));
const CalcParcelamento = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalcParcelamento })));
const CalcComissao = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalcComissao })));
const EntradasSaidas = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.EntradasSaidas })));
const ControleClientes = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ControleClientes })));
const SimuladorLucro = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SimuladorLucro })));
const ReservaEmergencia = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ReservaEmergencia })));
const SeparadorDespesas = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SeparadorDespesas })));
const GeradorRecibo = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.GeradorRecibo })));
const GeradorContrato = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.GeradorContrato })));
const MensagensCobranca = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.MensagensCobranca })));
const ValidadorCPF = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ValidadorCPF })));
const CalcFrete = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalcFrete })));
const VerificadorCNAE = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.VerificadorCNAE })));
const CalendarioMEI = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalendarioMEI })));
const TabelaDAS = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.TabelaDAS })));
const ChecklistAbertura = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ChecklistAbertura })));
const LinksOficiais = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.LinksOficiais })));
const SimuladorAposentadoriaMEI = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SimuladorAposentadoriaMEI })));
const CalculadoraINSSMEI = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.CalculadoraINSSMEI })));
const SimuladorDASAtraso = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.SimuladorDASAtraso })));
const GeradorOrcamentoPDF = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.GeradorOrcamentoPDF })));
const ComparadorContaPJ = lazy(() => import("@/components/tools/MoreTools").then(m => ({ default: m.ComparadorContaPJ })));

// Lazy-loaded SEO / Guide Components
const CnaeGuiaSEO = lazy(() => import("@/components/tools/CnaeGuiaSEO").then(m => ({ default: m.CnaeGuiaSEO })));
const DasMeiSEO = lazy(() => import("@/components/tools/DasMeiSEO").then(m => ({ default: m.DasMeiSEO })));
const DasMeiBeneficios = lazy(() => import("@/components/tools/DasMeiSEO").then(m => ({ default: m.DasMeiBeneficios })));
const DasMeiErros = lazy(() => import("@/components/tools/DasMeiSEO").then(m => ({ default: m.DasMeiErros })));
const DasMeiAutoridade = lazy(() => import("@/components/tools/DasMeiSEO").then(m => ({ default: m.DasMeiAutoridade })));
const PeopleAlsoAsk = lazy(() => import("@/components/tools/PeopleAlsoAsk").then(m => ({ default: m.PeopleAlsoAsk })));
const RelatedToolsDasMei = lazy(() => import("@/components/tools/RelatedToolsDasMei").then(m => ({ default: m.RelatedToolsDasMei })));

const MultaDasExemplos = lazy(() => import("@/components/tools/MultaDasSEO").then(m => ({ default: m.MultaDasExemplos })));
const MultaDasQuandoParcelar = lazy(() => import("@/components/tools/MultaDasSEO").then(m => ({ default: m.MultaDasQuandoParcelar })));
const MultaDasCronograma = lazy(() => import("@/components/tools/MultaDasSEO").then(m => ({ default: m.MultaDasCronograma })));
const MultaDasErros = lazy(() => import("@/components/tools/MultaDasSEO").then(m => ({ default: m.MultaDasErros })));
const MultaDasLinks = lazy(() => import("@/components/tools/MultaDasSEO").then(m => ({ default: m.MultaDasLinks })));

const LimiteMeiSEO = lazy(() => import("@/components/tools/LimiteMeiSEO").then(m => ({ default: m.LimiteMeiSEO })));
const IrpfMeiSEO = lazy(() => import("@/components/tools/IrpfMeiSEO").then(m => ({ default: m.IrpfMeiSEO })));
const SimuladorMaquinhaSEO = lazy(() => import("@/components/tools/SimuladorMaquinhaSEO").then(m => ({ default: m.SimuladorMaquinhaSEO })));

import { AuthorBlock } from "@/components/AuthorBlock";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { RelatedTools } from "@/components/RelatedTools";
import { RelatedArticles } from "@/components/RelatedArticles";
import { getRelatedArticlesForTool, getRelatedToolsForTool } from "@/lib/related";
import { ToolCtaIA } from "@/components/tools/ToolCtaIA";
import { RatingStars } from "@/components/RatingStars";

import { ShareButtons } from "@/components/ShareButtons";
import { getSeoExtension } from "@/content/tools-seo";
import { InfinitePayBanner } from "@/components/InfinitePayBanner";

type Content = {
  h1: string;
  metaTitle: string;
  metaDesc: string;
  intro: string;
  Tool: React.ComponentType;
  sections: { h2: string; body: string }[];
  faq: { q: string; a: string }[];
  paa?: { q: string; a: string }[];
};

const CONTENT: Record<string, Content> = {
  "calculadora-das-mei": {
    h1: "Calculadora DAS MEI 2026: Descubra o Valor Exato da Sua Contribuição",
    metaTitle: "Calculadora DAS MEI 2026 Atualizada | Valor Exato do DAS",
    metaDesc:
      "Calcule gratuitamente o valor do DAS MEI 2026. Descubra quanto pagar, entenda sua contribuição mensal e simule seu DAS atualizado em segundos.",
    intro:
      "Descubra o valor exato do DAS do seu MEI em 2026 conforme sua atividade. Simulação gratuita, atualizada e sem cadastro.",
    Tool: CalculadoraDAS,
    sections: [
      {
        h2: "O que é o DAS MEI?",
        body: 'O DAS (Documento de Arrecadação do <a href="http://www8.receita.fazenda.gov.br/SimplesNacional/" target="_blank" rel="noopener noreferrer">Simples Nacional</a>) é a <strong>guia única</strong> que todo Microempreendedor Individual paga mensalmente. Em um só boleto MEI, você recolhe a <strong>contribuição previdenciária</strong> ao INSS (5% do salário mínimo) e os impostos da atividade — ICMS para comércio e indústria, ISS para serviços. É o pagamento que mantém o CNPJ ativo, garante seus benefícios previdenciários e é obrigatório <strong>mesmo nos meses sem faturamento</strong>. O vencimento é sempre dia 20. Vale conferir também seu <a href="/ferramentas/limite-faturamento-mei">limite de faturamento anual</a> para garantir que você continua dentro do regime.',
      },
      {
        h2: "Quanto custa o DAS MEI em 2026?",
        body: "O <strong>valor DAS MEI 2026</strong> varia conforme a atividade: comércio e indústria pagam INSS + R$ 1,00 de ICMS (≈ R$ 76,90); prestação de serviços paga INSS + R$ 5,00 de ISS (≈ R$ 80,90); comércio + serviços paga INSS + ICMS + ISS (≈ R$ 81,90). O MEI Caminhoneiro recolhe 12% do salário mínimo ao INSS, com DAS aproximado de R$ 183,16 (transporte de cargas) ou R$ 187,16 (com ISS). Os valores são atualizados anualmente conforme o salário mínimo. Veja a tabela completa abaixo e use a calculadora acima para o valor exato do seu caso.",
      },
      {
        h2: "O que acontece se eu não pagar o DAS?",
        body: 'Atrasar o DAS gera <strong>multa de 0,33% ao dia</strong> (limitada a 20%) somada aos <strong>juros pela taxa Selic</strong> — calcule o impacto exato na <a href="/ferramentas/calculadora-multa-das">calculadora de multa e juros do DAS</a>. Acumulando atrasos, você perde benefícios do INSS (auxílio-doença, salário-maternidade, contagem para aposentadoria), pode cair em <strong>dívida ativa da União</strong>, ter o CNPJ excluído do regime MEI e enfrentar bloqueios em certidões negativas e contas bancárias PJ. Quem já tem débitos pode usar o <a href="/ferramentas/simulador-das-atraso">simulador de parcelamento do MEI</a>.',
      },
      {
        h2: "Como emitir a guia DAS MEI (passo a passo)",
        body: '1) Acesse o <a href="https://www.gov.br/empresas-e-negocios/pt-br/empreendedor" target="_blank" rel="noopener noreferrer">Portal do Empreendedor</a> ou o app MEI. 2) Entre no <strong>PGMEI</strong> com sua conta gov.br. 3) Selecione o ano e o período (mês de competência). 4) Clique em <strong>Emitir Guia</strong> — o boleto sai em PDF. 5) Pague pelo internet banking, Pix QR Code, débito automático, lotérica ou caixa do banco até o dia 20. Se o valor oficial divergir do calculado, confira sua atividade na CCMEI — a <a href="/ferramentas/ia-cnae-mei-2026">IA CNAE MEI</a> ajuda a validar em segundos.',
      },
    ],
    faq: [
      {
        q: "Qual o valor do DAS MEI em 2026?",
        a: "Em 2026: R$ 76,90 (comércio/indústria), R$ 80,90 (serviços) e R$ 81,90 (comércio + serviços). O MEI Caminhoneiro paga aproximadamente R$ 183,16. Os valores consideram 5% do salário mínimo para o INSS.",
      },
      {
        q: "Como emitir a guia DAS MEI?",
        a: "Acesse o Portal do Empreendedor (gov.br/mei) ou o app MEI, entre no PGMEI com gov.br, selecione o mês de competência e clique em 'Emitir Guia'. O boleto sai em PDF e pode ser pago por Pix, internet banking ou lotérica.",
      },
      {
        q: "Posso parcelar DAS atrasado?",
        a: "Sim. O MEI pode aderir ao parcelamento especial em até 60 vezes, diretamente no Portal do Simples Nacional ou no app MEI, com parcela mínima de R$ 50.",
      },
      {
        q: "O DAS muda todo ano?",
        a: "Sim. Como o INSS corresponde a 5% do salário mínimo vigente, sempre que o salário mínimo é reajustado, o valor do DAS muda — geralmente em janeiro.",
      },
      {
        q: "Caminhoneiro paga um DAS diferente?",
        a: "Sim. O MEI Caminhoneiro contribui com 12% do salário mínimo ao INSS (em vez de 5%), resultando em DAS mais alto: cerca de R$ 183,16 para transporte de cargas.",
      },
      {
        q: "O DAS inclui o INSS?",
        a: "Sim. A guia DAS reúne em um único boleto a contribuição previdenciária ao INSS e os impostos da atividade (ICMS e/ou ISS).",
      },
      {
        q: "Posso pagar DAS vencido?",
        a: "Sim. Você gera uma guia atualizada no PGMEI com multa de 0,33% ao dia (até 20%) e juros Selic já calculados automaticamente.",
      },
      {
        q: "O que acontece se atrasar o DAS?",
        a: "Multa diária, juros Selic, perda de benefícios do INSS, possível inclusão em dívida ativa e até exclusão do regime MEI em casos prolongados.",
      },
      {
        q: "O DAS é obrigatório mesmo sem faturamento?",
        a: "Sim. O DAS é obrigatório para manter o MEI regularizado, mesmo em meses sem faturamento, pois garante o CNPJ ativo e a contribuição ao INSS.",
      },
      {
        q: "Como consultar débitos do MEI?",
        a: "No Portal do Simples Nacional, entre em 'Consulta de Débitos do SIMEI' com sua conta gov.br. Lá aparecem todas as guias em aberto e em dívida ativa.",
      },
    ],
    paa: [
      {
        q: "Preciso pagar DAS mesmo sem faturar?",
        a: "Sim. O pagamento do DAS é obrigatório para manter o MEI regularizado, mesmo em meses sem faturamento.",
      },
      {
        q: "Posso pagar o DAS do ano inteiro de uma vez?",
        a: "Sim. É possível antecipar várias guias pelo PGMEI — útil para quem viaja ou prefere quitar tudo de uma vez.",
      },
      {
        q: "Os valores do DAS são oficiais?",
        a: "Sim. Usamos os valores divulgados pelo governo para 2026, baseados em 5% do salário mínimo vigente para o INSS, mais R$ 1,00 de ICMS e/ou R$ 5,00 de ISS.",
      },
    ],
  },
  "limite-faturamento-mei": {
    h1: "Limite de Faturamento MEI 2026: Quanto Ainda Posso Faturar Sem Estourar o Teto?",
    metaTitle: "Limite MEI 2026: Quanto Você Ainda Pode Faturar (Grátis)",
    metaDesc:
      "Calcule o limite MEI 2026: veja quanto já faturou, quanto ainda pode emitir em notas e o risco de desenquadramento. Teto atual: R$ 81.000/ano.",
    intro:
      "Descubra em segundos quanto você já faturou, quanto ainda pode emitir em notas fiscais e qual o risco de desenquadramento do MEI.",
    Tool: LimiteFaturamento,
    sections: [
      {
        h2: "Qual o limite de faturamento do MEI em 2026?",
        body: "O <strong>limite MEI 2026</strong> segue em <strong>R$ 81.000,00 por ano</strong>, equivalente a uma média de R$ 6.750 por mês de receita bruta. Esse teto MEI é aplicado ao faturamento bruto — ou seja, todo o dinheiro recebido pela atividade do MEI, antes de descontar custos, taxa de cartão, frete ou despesas operacionais. Pix, cartão, dinheiro, boleto e repasses de marketplace, tudo entra na conta.",
      },
      {
        h2: "Quanto posso faturar no MEI por mês?",
        body: 'Não existe limite mensal fixo. O que vale é o total no ano. Na prática, distribuir o <strong>faturamento anual MEI</strong> em torno de R$ 6.750/mês mantém o negócio confortável. Se em um mês você fatura R$ 12.000, basta reduzir o ritmo nos seguintes. Use a calculadora acima como termômetro e cruze com a <a href="/ferramentas/calculadora-das-mei">Calculadora DAS-MEI 2026</a> para projetar o caixa.',
      },
      {
        h2: "O que acontece se eu ultrapassar o limite MEI?",
        body: 'Existem duas faixas. Se você ultrapassar o teto MEI em até 20% (até R$ 97.200), paga DAS proporcional sobre o excedente e o <strong>desenquadramento MEI</strong> acontece apenas no ano seguinte. Se passar de 20%, o desenquadramento é retroativo a janeiro do mesmo ano — e você precisa recolher impostos como Microempresa (ME) sobre toda a receita bruta MEI desde o primeiro real. Simule o impacto no <a href="/ferramentas/simulador-desenquadramento">Simulador de Desenquadramento</a> antes de migrar.',
      },
      {
        h2: "Como evitar o desenquadramento do MEI",
        body: 'Acompanhe o faturamento mês a mês — não no fim do ano. Se em julho você já está com 60% do teto, ajuste o ritmo, antecipe contratos para o próximo exercício ou comece a planejar a migração para ME (limite de R$ 360 mil/ano). Quem está com dívidas em aberto pode usar o <a href="/ferramentas/simulador-das-atraso">Simulador de Parcelamento do MEI</a> para regularizar antes de migrar. E, se a sua atividade está cadastrada errada, vale checar com a <a href="/ferramentas/ia-cnae-mei-2026">IA CNAE MEI 2026</a> antes de emitir a próxima nota fiscal MEI.',
      },
    ],
    faq: [
      {
        q: "O limite é por CNPJ ou por pessoa?",
        a: "O limite é por CNPJ. Mas a Receita pode considerar 'sócio oculto' se você abrir vários MEIs em nome de familiares para fugir do teto.",
      },
      {
        q: "Faturamento inclui o que recebo via Pix?",
        a: "Sim. Todo dinheiro recebido pela atividade do MEI conta — independente do meio (Pix, dinheiro, cartão, transferência).",
      },
      {
        q: "O limite é proporcional se abri o MEI no meio do ano?",
        a: "Sim. Se você abriu o CNPJ em julho, por exemplo, seu limite proporcional é de R$ 6.750 × 6 = R$ 40.500 até dezembro.",
      },
    ],
  },
  "calculadora-precificacao": {
    h1: "Calculadora de Precificação para MEI",
    metaTitle: "Calculadora de Precificação Grátis: Preço Certo em 1 Min",
    metaDesc:
      "Calculadora de precificação para MEI: defina o preço de venda do seu produto ou serviço considerando custos fixos, custos variáveis, impostos e margem de lucro.",
    intro:
      "Pare de chutar preço. Calcule o valor ideal considerando seus custos, impostos e a margem de lucro que você quer.",
    Tool: Precificacao,
    sections: [
      {
        h2: "Por que precificar errado quebra o MEI",
        body: "A maioria dos microempreendedores define preço olhando o concorrente — e descobre tarde demais que está trabalhando no prejuízo. Precificar é matemática: preço de venda precisa cobrir o custo do produto, as despesas variáveis (frete, embalagem, taxa do cartão), os impostos e ainda gerar a margem de lucro que sustenta o seu negócio.",
      },
      {
        h2: "A fórmula correta",
        body: "A fórmula é: Preço = (Custo + Despesas) ÷ (1 − Margem% − Impostos%). Diferente de simplesmente 'somar a margem ao custo', essa fórmula garante que a margem seja calculada sobre o preço final — e não sobre o custo. É assim que grandes empresas precificam.",
      },
      {
        h2: "Markup vs Margem: qual a diferença?",
        body: "Margem é o lucro como percentual do preço de venda (ex: 30% de margem em um produto de R$ 100 = R$ 30 de lucro). Markup é o multiplicador aplicado sobre o custo (ex: markup de 2× em um custo de R$ 50 = preço de R$ 100). A ferramenta acima mostra os dois para você comparar.",
      },
    ],
    faq: [
      {
        q: "Que margem devo usar?",
        a: "Depende do segmento. Serviços costumam trabalhar com 40-60% de margem; comércio físico com 20-40%; e-commerce com 25-50%. Comece com 30% e ajuste conforme a aceitação do mercado.",
      },
      {
        q: "Devo incluir o DAS no cálculo?",
        a: "Sim. Some o DAS mensal nas suas despesas fixas e divida pelo número médio de vendas para encontrar o custo por unidade.",
      },
      {
        q: "E a taxa do cartão de crédito?",
        a: "Inclua nos impostos/taxas. Maquininhas costumam cobrar 2-4% no débito e 3-6% no crédito à vista.",
      },
    ],
  },
  "validador-cnpj": {
    h1: "Validador de CNPJ Online",
    metaTitle: "Validador de CNPJ Grátis Online: Confira Agora",
    metaDesc:
      "Validador de CNPJ grátis e instantâneo: verifica os dígitos conforme a regra oficial da Receita Federal, direto no navegador e sem cadastro nem envio de dados.",
    intro:
      "Verifique em segundos se um CNPJ é matematicamente válido — antes de emitir uma nota, fechar contrato ou enviar produto.",
    Tool: ValidadorCNPJ,
    sections: [
      {
        h2: "Como funciona a validação de CNPJ",
        body: "Todo CNPJ tem 14 dígitos, sendo os dois últimos chamados de 'dígitos verificadores' (DV). Eles são calculados a partir dos 12 primeiros dígitos por uma fórmula matemática oficial da Receita Federal. Se você digitar qualquer número errado, os DV não baterão e o CNPJ será considerado inválido.",
      },
      {
        h2: "Validação não é o mesmo que ativo",
        body: "Atenção: este validador confere apenas se a estrutura matemática do número está correta. Para saber se o CNPJ está ativo, baixado, suspenso ou inapto, é preciso consultar o site da Receita Federal (solucoes.receita.fazenda.gov.br/Servicos/cnpjreva).",
      },
      {
        h2: "Quando validar antes é essencial",
        body: "Sempre valide o CNPJ antes de: emitir nota fiscal para um cliente novo, assinar contrato de prestação de serviços, despachar mercadoria para um endereço de cobrança, ou cadastrar um fornecedor. Um simples erro de digitação pode invalidar uma nota e gerar problemas com o fisco.",
      },
    ],
    faq: [
      {
        q: "Vocês consultam a Receita Federal?",
        a: "Não. Esta ferramenta apenas valida a matemática dos dígitos. Para consultar situação cadastral, vá ao site da Receita.",
      },
      {
        q: "Meus dados são salvos?",
        a: "Não. O CNPJ digitado é validado direto no seu navegador e nunca é enviado para nenhum servidor.",
      },
      {
        q: "Funciona com pontuação?",
        a: "Sim. Você pode digitar com ou sem pontos, barras e traços — a ferramenta formata automaticamente.",
      },
    ],
  },

  /* ===== CALCULADORAS ===== */
  "calculadora-multa-das": {
    h1: "Calculadora de Multa do DAS",
    metaTitle: "Multa do DAS MEI 2026: Calcule Atraso e Juros Grátis",
    metaDesc:
      "Calcule a multa por atraso no pagamento do DAS-MEI: 0,33% ao dia (limitada a 20% do valor) somada aos juros pela taxa Selic do mês da regularização do boleto.",
    intro:
      "Atrasou o DAS? Descubra exatamente quanto pagar com multa e juros antes de gerar a guia DAS vencida.",
    Tool: CalculadoraMultaDAS,
    sections: [
      {
        h2: "Como funciona a multa do DAS atrasado",
        body: 'Quando o microempreendedor deixa de pagar a guia DAS no vencimento (dia 20), a Receita Federal aplica multa de 0,33% por dia de atraso, limitada a 20% sobre o valor original do boleto. Em paralelo, incidem juros pela taxa Selic acumulada do mês da regularização. Quanto mais cedo você calcular DAS atrasado e quitar, menor o impacto — use a <a href="/ferramentas/calculadora-das-mei">Calculadora DAS-MEI 2026</a> para conferir também o valor da guia corrente do mês.',
      },
      {
        h2: "Pode parcelar o DAS atrasado?",
        body: 'Sim. Débitos do MEI podem ser parcelados em até 60 meses pelo Portal do Simples Nacional, com parcela mínima vigente. É a alternativa mais usada quando o atraso DAS MEI passa de 6 meses ou quando a soma já configura uma dívida ativa MEI relevante. Simule o valor da prestação na <a href="/ferramentas/simulador-das-atraso">ferramenta de parcelamento do MEI</a> antes de aderir.',
      },
      {
        h2: "O atraso prejudica meu INSS?",
        body: "Sim. Cada mês de DAS atrasado só passa a contar para aposentadoria e demais benefícios depois de pago. Acumular muitos meses devendo faz perder a qualidade de segurado, suspendendo auxílio-doença e salário-maternidade. Por isso, regularizar MEI rapidamente é mais importante do que esperar 'a Selic cair'.",
      },
      {
        h2: "Como regularizar o MEI quando o débito é alto",
        body: 'Para débitos acima de R$ 5.000, o caminho recomendado é parcelar e manter o DAS atual em dia em paralelo. Avalie também o seu faturamento na <a href="/ferramentas/limite-faturamento-mei">calculadora de limite de faturamento MEI</a>: se você estiver perto do teto anual, o desenquadramento somado à dívida ativa pode forçar a migração para Microempresa antes do planejado.',
      },
    ],
    faq: [
      {
        q: "O cálculo da multa do DAS é exato?",
        a: "É uma estimativa muito próxima. O valor oficial só sai quando você emite a guia DAS atualizada no Portal do Empreendedor, porque os juros do DAS dependem da Selic acumulada até o mês do pagamento.",
      },
      {
        q: "Posso pagar só a multa do DAS atrasado?",
        a: "Não. A guia DAS vencida já é emitida com o valor original + multa do DAS + juros somados em um único boleto. Não existe pagamento separado.",
      },
      {
        q: "Tem desconto para quitar o débito MEI à vista?",
        a: "Os parcelamentos especiais (PERT, Relp) eventualmente oferecem reduções de multa e juros. Já o parcelamento convencional pelo Simples Nacional não oferece desconto, apenas o prazo estendido.",
      },
      {
        q: "Em quanto tempo o atraso DAS MEI vira dívida ativa?",
        a: "Em geral, débitos não regularizados dentro do exercício seguinte são inscritos em dívida ativa da União e passam a ser cobrados pela PGFN, com possibilidade de protesto em cartório.",
      },
    ],
  },

  "simulador-irpf-mei": {
    h1: "IRPF MEI 2026: Preciso Declarar Imposto de Renda? Simule Agora",
    metaTitle: "IRPF MEI 2026: Preciso Declarar? Simulador Grátis Online",
    metaDesc:
      "Descubra se o MEI precisa declarar IRPF em 2026. Simulador grátis calcula renda tributável, isenção por atividade e estimativa do imposto de renda.",
    intro:
      "Descubra em segundos se você precisa entregar a declaração de Imposto de Renda, quanto da sua receita é tributável e qual a estimativa de IRPF.",
    Tool: SimuladorIRPF,
    sections: [
      {
        h2: "MEI precisa declarar Imposto de Renda?",
        body: "Ser MEI não isenta automaticamente do <strong>imposto de renda pessoa física</strong>. O CNPJ entrega a <strong>DASN-SIMEI</strong>; já você, pessoa física, pode precisar entregar o IRPF se a renda tributável anual ultrapassar R$ 30.639,90 — somando lucro não isento do MEI, salário CLT, aluguéis e investimentos. O simulador acima estima o cenário em segundos, antes de abrir o programa da Receita Federal.",
      },
      {
        h2: "Quanto do faturamento MEI é isento de IR?",
        body: 'A Receita aplica um percentual de isenção sobre o faturamento conforme a atividade: <strong>8% para comércio e indústria</strong>, <strong>16% para transporte de passageiros</strong> e <strong>32% para serviços</strong>. O que ultrapassar esses percentuais entra como <strong>renda tributável MEI</strong> e soma a outras rendas tributáveis no IRPF. Antes de simular, confirme se o seu CNAE bate com a atividade real usando a <a href="/ferramentas/ia-cnae-mei-2026">IA CNAE MEI 2026</a>.',
      },
      {
        h2: "DASN-SIMEI x IRPF: qual é qual?",
        body: "A <strong>DASN-SIMEI</strong> é a declaração anual do CNPJ MEI, obrigatória para todos, com prazo até 31 de maio. O <strong>IRPF</strong> é da sua pessoa física e só é exigido em situações específicas. Confundir as duas leva muita gente à malha fina por entregar uma e esquecer a outra. Veja o quadro comparativo logo abaixo para entender qual exige o quê.",
      },
      {
        h2: "Quando o MEI cai na malha fina?",
        body: 'Os erros mais comuns na <strong>declaração MEI 2026</strong> são: omitir rendimentos CLT do mesmo ano, esquecer aluguéis recebidos, ignorar operações em bolsa ou cripto e declarar o faturamento bruto inteiro como tributável (em vez de aplicar a isenção legal). Antes de declarar, vale rodar o <a href="/ferramentas/limite-faturamento-mei">Calculadora de Limite MEI</a> e a <a href="/ferramentas/calculadora-das-mei">Calculadora DAS-MEI 2026</a> para reconciliar valores.',
      },
    ],
    faq: [
      {
        q: "Esse simulador substitui o do governo?",
        a: "Não. É uma estimativa para planejamento. A declaração oficial é feita no programa da Receita Federal.",
      },
      {
        q: "Posso deduzir despesas da empresa?",
        a: "Não no IRPF da pessoa física. Despesas do MEI não viram dedução no seu IR pessoal.",
      },
    ],
  },
  "simulador-desenquadramento": {
    h1: "Simulador de Desenquadramento MEI",
    metaTitle: "MEI → ME: Simulador de Desenquadramento Grátis 2026",
    metaDesc:
      "Simule o impacto financeiro de virar Microempresa (ME) e sair do regime MEI em 2026. Compare impostos lado a lado e veja o melhor momento para migrar de regime.",
    intro:
      "Vai estourar o limite de faturamento? Veja quanto pagaria de imposto como Microempresa antes de migrar.",
    Tool: SimuladorDesenquadramento,
    sections: [
      {
        h2: "Quando o MEI precisa virar ME?",
        body: "Quando o faturamento ultrapassa R$ 81 mil/ano. Até 20% acima (R$ 97.200), o desenquadramento é no ano seguinte. Acima disso, retroativo a janeiro.",
      },
      {
        h2: "Vantagens de virar ME",
        body: "Limite de R$ 360 mil/ano (ME) ou R$ 4,8 milhões (EPP), permissão para ter sócios, contratar mais funcionários e abrir filiais.",
      },
      {
        h2: "Desvantagens",
        body: "Imposto proporcional ao faturamento (4% a 17% pelo Simples), obrigatoriedade de contador e mais obrigações acessórias.",
      },
    ],
    faq: [
      {
        q: "Posso voltar a ser MEI depois?",
        a: "Sim, desde que o faturamento caia abaixo do limite e você cumpra o prazo de 1 ano fora do regime.",
      },
      {
        q: "Preciso de contador no Simples?",
        a: "Sim. Para ME e EPP a contabilidade é obrigatória.",
      },
    ],
  },

  /* ===== PRECIFICAÇÃO ===== */
  "valor-da-hora": {
    h1: "Calculadora de Valor da Hora",
    metaTitle: "Valor da Hora 2026: Quanto Cobrar por Hora (Grátis)",
    metaDesc:
      "Descubra quanto cobrar por hora de trabalho como MEI considerando a renda mensal desejada, as horas faturáveis no mês, os impostos do Simples e despesas fixas.",
    intro:
      "Pare de cobrar barato. Calcule o valor da sua hora de trabalho com base no que você realmente quer ganhar.",
    Tool: ValorDaHora,
    sections: [
      {
        h2: "Por que sua hora vale mais do que você imagina",
        body: "Freelancers tendem a cobrar pouco porque calculam só o tempo de execução — esquecendo prospecção, reuniões, ajustes, impostos e férias. O valor justo da hora considera todos esses fatores.",
      },
      {
        h2: "Quantas horas posso faturar por mês?",
        body: "Mesmo trabalhando 8h/dia, raramente são 100% faturáveis. Considere 60-70% como tempo realmente vendável (cerca de 100 a 130h/mês para quem trabalha em tempo integral).",
      },
    ],
    faq: [
      {
        q: "Devo cobrar a mesma hora para todos clientes?",
        a: "Não necessariamente. Projetos urgentes, complexos ou de longo prazo podem ter precificação diferente.",
      },
    ],
  },
  "margem-markup": {
    h1: "Calculadora de Margem e Markup",
    metaTitle: "Margem e Markup: Calculadora Grátis Online",
    metaDesc:
      "Calculadora de margem e markup grátis: converta entre margem e markup, entenda a diferença entre os conceitos e precifique seus produtos com mais clareza.",
    intro: "Margem ou markup? Calcule os dois e entenda qual é mais útil para o seu negócio.",
    Tool: MargemMarkup,
    sections: [
      {
        h2: "A diferença essencial",
        body: "Margem é o lucro como porcentagem do PREÇO DE VENDA. Markup é o multiplicador aplicado sobre o CUSTO. Um produto com markup de 100% tem margem de 50%.",
      },
      {
        h2: "Quando usar cada um",
        body: "Use markup para definir preço a partir do custo (precificação). Use margem para analisar rentabilidade comparando produtos.",
      },
    ],
    faq: [
      {
        q: "Margem pode passar de 100%?",
        a: "Não. Margem é sempre entre 0% e 100% — o lucro nunca é maior que o preço total.",
      },
    ],
  },
  "calculadora-desconto": {
    h1: "Calculadora de Desconto",
    metaTitle: "Calculadora de Desconto Grátis: Preço Final em Segundos",
    metaDesc:
      "Calculadora de desconto grátis: informe preço e percentual para ver o valor final, quanto o cliente economiza e o impacto real na sua margem de lucro.",
    intro: "Calcule preço final, valor economizado e veja se o desconto cabe na sua margem.",
    Tool: CalcDesconto,
    sections: [
      {
        h2: "Cuidado ao dar desconto",
        body: "Desconto sai direto da sua margem de lucro. Um desconto de 20% em um produto com 30% de margem reduz seu lucro real para apenas 10%.",
      },
    ],
    faq: [
      {
        q: "Como dar desconto sem prejuízo?",
        a: "Calcule sempre a margem ANTES de aceitar o desconto. Se a margem ficar abaixo de 10%, recuse ou negocie outras condições.",
      },
    ],
  },
  "calculadora-parcelamento": {
    h1: "Calculadora de Parcelamento com Juros",
    metaTitle: "Parcelamento no Cartão: Calculadora de Juros Grátis",
    metaDesc:
      "Use a nossa calculadora de parcelamento online e gratuita: simule vendas no cartão com juros pela Tabela Price e saiba o valor líquido de cada parcela.",
    intro: "Saiba o valor de cada parcela e o total recebido ao vender no cartão de crédito.",
    Tool: CalcParcelamento,
    sections: [
      {
        h2: "Tabela Price",
        body: "O cálculo segue o sistema Price: parcelas iguais com juros compostos. É o padrão usado por maquininhas e bancos no Brasil.",
      },
      {
        h2: "Antecipar recebíveis vale a pena?",
        body: "Maquininhas cobram entre 1% e 4% para antecipar. Antes de antecipar, compare com o custo de um capital de giro no banco — pode sair mais barato.",
      },
    ],
    faq: [
      {
        q: "Por que recebo menos do que vendi?",
        a: "A maquininha desconta a taxa antes de creditar. Considere isso na sua precificação.",
      },
    ],
  },
  "calculadora-comissao": {
    h1: "Calculadora de Comissão",
    metaTitle: "Calculadora de Comissão Grátis: Calcule sobre Vendas",
    metaDesc:
      "Calculadora de comissão grátis: calcule rapidamente a comissão de vendedores, parceiros ou afiliados sobre a venda e veja quanto sobra líquido no seu MEI.",
    intro: "Calcule rapidamente quanto pagar de comissão e quanto sobra líquido para você.",
    Tool: CalcComissao,
    sections: [
      {
        h2: "Comissão estimula vendas",
        body: "Em vez de salário fixo alto, pagar comissão alinha o vendedor ao seu resultado. O percentual médio varia entre 5% e 15% no varejo, podendo chegar a 30% em produtos de alto valor agregado.",
      },
    ],
    faq: [
      {
        q: "Comissão entra no faturamento do MEI?",
        a: "Sim. Toda receita bruta antes de pagar comissão conta no limite anual do MEI.",
      },
    ],
  },

  /* ===== FINANCEIRO ===== */
  "controle-entradas-saidas": {
    h1: "Controle de Entradas e Saídas",
    metaTitle: "Fluxo de Caixa MEI Grátis: Entradas e Saídas Online",
    metaDesc:
      "Controle de fluxo de caixa do MEI: registre entradas e saídas em uma planilha simples, sem cadastro, com os dados salvos com segurança direto no seu navegador.",
    intro:
      "Anote tudo o que entra e sai. Sem planilha complicada, sem cadastro — apenas o essencial.",
    Tool: EntradasSaidas,
    sections: [
      {
        h2: "Por que controlar todo dia",
        body: "MEIs que registram receitas e despesas diariamente conseguem prever crises com até 60 dias de antecedência. Sem controle, qualquer imprevisto vira emergência.",
      },
      {
        h2: "Categorize suas saídas",
        body: "Separe pelo menos: matéria-prima, marketing, taxas/impostos, retirada pessoal. Isso revela onde seu dinheiro está realmente indo.",
      },
    ],
    faq: [
      {
        q: "Os dados ficam salvos?",
        a: "Sim, no seu próprio navegador (localStorage). Se limpar o cache ou trocar de dispositivo, os dados se perdem — exporte regularmente.",
      },
      {
        q: "Posso exportar?",
        a: "A exportação para CSV/PDF está em desenvolvimento. Por enquanto, copie manualmente.",
      },
    ],
  },
  "controle-clientes": {
    h1: "Controle de Clientes",
    metaTitle: "CRM para MEI Grátis: Controle de Clientes Online",
    metaDesc:
      "Cadastre e organize seus clientes em um mini CRM grátis para MEI: nome, telefone, e-mail e observações em um lugar, salvos com segurança no próprio navegador.",
    intro:
      "Tenha todos os seus clientes em um só lugar — telefone, e-mail e observações importantes.",
    Tool: ControleClientes,
    sections: [
      {
        h2: "Por que ter cadastro de clientes",
        body: "Vender para um cliente que já comprou custa até 7× menos que conquistar um novo. Mantenha a base organizada para fazer follow-up, divulgar promoções e fortalecer relacionamento.",
      },
    ],
    faq: [
      {
        q: "Quantos clientes posso cadastrar?",
        a: "Não há limite. Os dados ficam no seu próprio dispositivo.",
      },
    ],
  },
  "simulador-lucro": {
    h1: "Simulador de Lucro Mensal",
    metaTitle: "Lucro Mensal do MEI: Simulador Grátis em 1 Minuto",
    metaDesc:
      "Simulador de lucro do MEI: calcule o lucro mensal real considerando receita bruta, custos variáveis, despesas fixas e o valor do DAS do Simples Nacional.",
    intro: "Veja em segundos qual é o seu lucro líquido depois de pagar todas as contas e o DAS.",
    Tool: SimuladorLucro,
    sections: [
      {
        h2: "Faturamento não é lucro",
        body: "Muitos MEIs confundem o que entra com o que sobra. Lucro real é receita menos TODOS os custos: matéria-prima, transporte, taxas, DAS, aluguel, internet, energia.",
      },
    ],
    faq: [
      {
        q: "Margem de quanto é boa?",
        a: "Depende do segmento. Serviços costumam ter 30-50%, comércio físico 15-25%, alimentação 10-20%.",
      },
    ],
  },
  "reserva-emergencia": {
    h1: "Calculadora de Reserva de Emergência",
    metaTitle: "Reserva de Emergência MEI: Calcule Quanto Guardar",
    metaDesc:
      "Calculadora de reserva de emergência: descubra quanto você precisa ter guardado para cobrir 6 a 12 meses de despesas pessoais e do MEI em caso de imprevistos.",
    intro: "Quanto você precisa ter guardado para sobreviver 6 meses sem faturar nada?",
    Tool: ReservaEmergencia,
    sections: [
      {
        h2: "Por que MEI precisa de mais reserva",
        body: "Sua renda é variável. Diferente de um CLT, você não tem FGTS, seguro-desemprego ou décimo terceiro garantido. Reserva de 6-12 meses é o mínimo recomendado.",
      },
      {
        h2: "Onde guardar",
        body: "Tesouro Selic, CDBs de liquidez diária e contas remuneradas oferecem rentabilidade próxima ao CDI com saque imediato. Evite poupança.",
      },
    ],
    faq: [
      {
        q: "Posso usar a reserva para investir no MEI?",
        a: "Não. Reserva é só para emergências. Investimento no negócio sai de outro 'bolso' (capital de giro).",
      },
    ],
  },
  "separador-despesas-mei": {
    h1: "Separador de Despesas CPF vs CNPJ",
    metaTitle: "CPF x CNPJ: Separe Gastos Pessoais e do MEI Grátis",
    metaDesc:
      "Separe gastos pessoais (CPF) dos gastos da empresa (CNPJ) em uma ferramenta simples: indispensável para o MEI organizado entender o lucro real e tomar decisões.",
    intro:
      "Pare de misturar conta pessoal com conta da empresa — é o erro número 1 do MEI iniciante.",
    Tool: SeparadorDespesas,
    sections: [
      {
        h2: "Por que separar é vital",
        body: "Misturar finanças pessoais e do MEI esconde o lucro real, atrapalha a apuração de impostos e dificulta tomar decisões. Tenha conta bancária PJ separada — vários bancos digitais oferecem grátis.",
      },
    ],
    faq: [
      {
        q: "Posso usar minha conta PF para o MEI?",
        a: "Tecnicamente sim, mas é desaconselhado. A separação clara é a base de qualquer controle financeiro.",
      },
    ],
  },

  /* ===== DOCUMENTOS ===== */
  "gerador-recibo": {
    h1: "Gerador de Recibo MEI",
    metaTitle: "Gerador de Recibo Grátis: PDF Pronto em 1 Minuto",
    metaDesc:
      "Gerador de recibo grátis para MEI: crie recibos profissionais em segundos, modelo editável com CNPJ e logo, pronto para imprimir ou salvar em PDF assinado.",
    intro:
      "Preencha, imprima e entregue. Recibo simples, válido e profissional em menos de 1 minuto.",
    Tool: GeradorRecibo,
    sections: [
      {
        h2: "Recibo substitui nota fiscal?",
        body: "Não. Recibo é uma declaração de pagamento, mas não substitui a NFS-e quando ela é exigida. Para serviços a pessoa jurídica, emita sempre a nota fiscal pela prefeitura.",
      },
      {
        h2: "Quando usar recibo",
        body: "Em vendas para pessoa física, em trocos eventuais, ou como comprovante adicional junto com a nota fiscal.",
      },
    ],
    faq: [
      {
        q: "Para imprimir, como faço?",
        a: "Clique em 'Imprimir / Salvar PDF' — abre a janela do navegador onde você pode imprimir ou salvar como PDF.",
      },
      {
        q: "Tem valor legal?",
        a: "Sim, recibo simples tem validade entre as partes. Para mais segurança, reconheça firma em cartório.",
      },
    ],
  },
  "gerador-contrato": {
    h1: "Gerador de Contrato Simples",
    metaTitle: "Contrato de Prestação de Serviço Grátis: Modelo MEI",
    metaDesc:
      "Gerador de contrato de prestação de serviços para MEI: modelo editável com cláusulas essenciais, valor, prazos e multa, pronto para imprimir ou enviar em PDF.",
    intro:
      "Modelo simples e direto de contrato de prestação de serviços. Edite, imprima e proteja seu trabalho.",
    Tool: GeradorContrato,
    sections: [
      {
        h2: "Por que contrato é importante",
        body: "Mesmo informalmente, um contrato escrito evita conflitos, deixa claro o escopo do serviço, prazo e forma de pagamento. É sua proteção em caso de inadimplência.",
      },
      {
        h2: "Esse contrato é definitivo?",
        body: "É um modelo simplificado. Para projetos de alto valor ou complexidade, consulte um advogado para personalizar cláusulas (multa, propriedade intelectual, confidencialidade).",
      },
    ],
    faq: [
      {
        q: "Precisa reconhecer firma?",
        a: "Não obrigatório, mas recomendado para contratos de valor expressivo.",
      },
    ],
  },
  "mensagem-cobranca": {
    h1: "Mensagens de Cobrança para WhatsApp",
    metaTitle: "Mensagens de Cobrança WhatsApp Prontas para Copiar",
    metaDesc:
      "Modelos de mensagem prontos para cobrar clientes inadimplentes via WhatsApp de forma educada e profissional, sem perder o cliente e mantendo a boa relação.",
    intro:
      "Cobrar pode ser desconfortável. Use mensagens prontas, educadas e eficazes para receber sem perder o cliente.",
    Tool: MensagensCobranca,
    sections: [
      {
        h2: "A arte de cobrar bem",
        body: "Cobrança não precisa ser agressiva. Pesquisas mostram que abordagens cordiais têm taxa de sucesso 3× maior que mensagens hostis.",
      },
      {
        h2: "Quando escalar",
        body: "Se após 3 tentativas amigáveis não houver retorno, formalize por e-mail e considere protesto extrajudicial em cartório (custo baixo, alta eficácia).",
      },
    ],
    faq: [
      {
        q: "Posso cobrar juros?",
        a: "Sim, desde que previsto em contrato ou nota. Limite: 1% ao mês para juros + multa de até 2%.",
      },
    ],
  },

  /* ===== UTILITÁRIOS ===== */
  "validador-cpf": {
    h1: "Validador de CPF Online",
    metaTitle: "Validador de CPF Grátis Online: Verifique Agora",
    metaDesc:
      "Validador de CPF gratuito e instantâneo: verifica se o número segue o cálculo oficial dos dígitos da Receita Federal, direto no navegador e sem cadastro.",
    intro: "Confirme se um CPF é matematicamente válido antes de emitir nota ou cadastrar cliente.",
    Tool: ValidadorCPF,
    sections: [
      {
        h2: "Validação ≠ situação cadastral",
        body: "Esta ferramenta confere apenas se os dígitos batem. Para saber se o CPF está regular, consulte o site da Receita Federal.",
      },
    ],
    faq: [
      {
        q: "Vocês guardam o CPF digitado?",
        a: "Não. Toda validação acontece no seu navegador, sem envio de dados.",
      },
    ],
  },
  "calculadora-frete": {
    h1: "Calculadora de Frete Simples",
    metaTitle: "Calculadora de Frete Grátis: Preço Justo por Entrega",
    metaDesc:
      "Calculadora de frete: estime fretes locais por distância e peso da carga. Útil para entregadores, lojistas, prestadores de serviço e MEIs que vendem online.",
    intro:
      "Estimativa rápida de frete para entregas locais, considerando distância, peso e taxa fixa de saída.",
    Tool: CalcFrete,
    sections: [
      {
        h2: "Como precificar frete justo",
        body: "Não cobre só o combustível. Inclua tempo, desgaste do veículo, e uma margem para imprevistos. Cobrar barato demais inviabiliza o serviço a médio prazo.",
      },
    ],
    faq: [
      {
        q: "Funciona para Correios?",
        a: "Não. Esta é uma estimativa para entregas próprias. Para Correios use a calculadora oficial em correios.com.br.",
      },
    ],
  },
  "verificador-cnae": {
    h1: "Verificador de CNAE para MEI",
    metaTitle: "CNAE Permitido MEI 2026: Verificador Grátis Online",
    metaDesc:
      "Verifique se uma atividade (CNAE) é permitida para o MEI em 2026 com a lista atualizada das ocupações mais comuns, profissões proibidas e exemplos na prática.",
    intro:
      "Confira se sua atividade pode ser exercida como MEI. Listamos os CNAEs mais procurados.",
    Tool: VerificadorCNAE,
    sections: [
      {
        h2: "O que é CNAE",
        body: "Classificação Nacional de Atividades Econômicas. Cada negócio tem um código que define o que ele pode fazer e quais impostos paga.",
      },
      {
        h2: "Atividades NÃO permitidas",
        body: "Médicos, engenheiros, advogados, dentistas e outras profissões regulamentadas geralmente não podem ser MEI. Confira a lista oficial completa no Portal do Empreendedor.",
      },
    ],
    faq: [
      {
        q: "Posso ter mais de uma atividade?",
        a: "Sim, o MEI pode ter 1 atividade principal + até 15 secundárias, todas permitidas para o regime.",
      },
    ],
  },

  /* ===== RECURSOS ===== */
  "calendario-mei": {
    h1: "Calendário do MEI 2026: Todas as Datas Importantes do Ano",
    metaTitle: "Calendário MEI 2026: Todas as Datas Importantes",
    metaDesc:
      "Calendário do MEI 2026: todas as datas importantes do ano, com vencimentos do DAS, prazo da declaração DASN-SIMEI e ajustes que afetam o microempreendedor.",
    intro:
      "Não perca nenhum prazo. Aqui estão todas as datas importantes do MEI em 2026 — vencimentos do DAS mês a mês e o prazo da DASN-SIMEI.",
    Tool: CalendarioMEI,
    sections: [
      {
        h2: "Vencimento do DAS MEI — todo dia 20",
        body: 'O <strong>DAS MEI</strong> vence sempre no dia 20 de cada mês. Se a data cair em fim de semana ou feriado, o vencimento é prorrogado para o próximo dia útil. Emita sua guia no <a href="https://www8.receita.fazenda.gov.br/SimplesNacional/" target="_blank" rel="noopener noreferrer">PGMEI</a> ou calcule o valor exato na <a href="/ferramentas/calculadora-das-mei">Calculadora DAS-MEI 2026</a>.',
      },
      {
        h2: "DASN-SIMEI 2026 — prazo até 31 de maio",
        body: "A <strong>declaração anual DASN-SIMEI</strong>, referente ao faturamento do ano anterior, deve ser entregue até o último dia útil de maio. Quem perde o prazo paga multa mínima de R$ 50 e pode ter o CNPJ bloqueado para emissão de novas guias.",
      },
      {
        h2: "Datas importantes do MEI em 2026 (resumo)",
        body: 'Além do DAS mensal (dia 20) e da DASN-SIMEI (até 31/05), fique de olho no <strong>reajuste do salário mínimo em janeiro</strong> — que altera o valor do DAS de todo o ano — e na renovação do alvará municipal, quando exigido pela sua prefeitura. Para acompanhar o teto anual, use o <a href="/ferramentas/limite-faturamento-mei">Limite de Faturamento MEI 2026</a>.',
      },
    ],
    faq: [
      {
        q: "Qual o vencimento do DAS MEI em 2026?",
        a: "O DAS MEI vence sempre no dia 20 de cada mês. Quando o dia 20 cai em sábado, domingo ou feriado, o vencimento é prorrogado para o próximo dia útil.",
      },
      {
        q: "Qual o prazo da DASN-SIMEI 2026?",
        a: "A DASN-SIMEI referente ao ano-calendário 2025 deve ser entregue até 31 de maio de 2026, pelo Portal do Simples Nacional.",
      },
      {
        q: "Posso pagar DAS antes do vencimento?",
        a: "Sim, é possível antecipar várias guias de uma vez pelo PGMEI. Não há desconto pela antecipação, mas evita esquecimento e atrasos.",
      },
    ],
  },
  "tabela-das-2026": {
    h1: "Tabela DAS-MEI 2026 Oficial",
    metaTitle: "Tabela DAS MEI 2026 Atualizada: Valores Oficiais",
    metaDesc:
      "Tabela completa do DAS-MEI 2026 com valores oficiais por atividade: comércio, indústria, prestação de serviços, transporte de carga e MEI Caminhoneiro.",
    intro:
      "Veja em um piscar de olhos quanto custa o DAS para cada tipo de atividade do MEI em 2026.",
    Tool: TabelaDAS,
    sections: [
      {
        h2: "Como o valor é composto",
        body: "INSS (5% do salário mínimo) + ICMS (R$ 1, comércio/indústria) + ISS (R$ 5, serviços). MEI Caminhoneiro paga 12% de INSS, com valor maior.",
      },
    ],
    faq: [
      {
        q: "Esses valores mudam no ano?",
        a: "Sim, sempre que o salário mínimo é reajustado. Os valores aqui valem para 2026.",
      },
    ],
  },
  "checklist-abertura": {
    h1: "Checklist de Abertura do MEI",
    metaTitle: "Como Abrir MEI 2026: Checklist Passo a Passo Grátis",
    metaDesc:
      "Checklist completo para abrir o seu MEI em 2026: tudo o que você precisa fazer, do cadastro no gov.br à emissão do CNPJ e o pagamento do primeiro DAS do mês.",
    intro: "Passo a passo para abrir e regularizar seu MEI sem esquecer nada importante.",
    Tool: ChecklistAbertura,
    sections: [
      {
        h2: "Quanto tempo leva",
        body: "A abertura online leva cerca de 15 minutos. O CNPJ é gerado na hora. Já a regularização municipal (alvará) pode demorar de 1 a 30 dias dependendo da cidade.",
      },
      {
        h2: "É grátis abrir MEI?",
        body: "Sim, totalmente grátis pelo Portal do Empreendedor. Cuidado com sites que cobram para fazer o procedimento que você mesmo pode fazer.",
      },
    ],
    faq: [
      {
        q: "Posso abrir MEI sendo CLT?",
        a: "Sim, exceto se for servidor público federal em ativa.",
      },
    ],
  },
  "links-oficiais": {
    h1: "Central de Links Oficiais do MEI",
    metaTitle: "Links Oficiais do MEI 2026: Tudo do Governo num Lugar",
    metaDesc:
      "Central de links oficiais do MEI: acesso rápido aos serviços do governo para emitir DAS, consultar CNPJ, fazer a declaração anual DASN-SIMEI e alterar dados.",
    intro:
      "Todos os links oficiais que o MEI usa, organizados em um só lugar — sem perder tempo procurando.",
    Tool: LinksOficiais,
    sections: [
      {
        h2: "Por que confiar só em fontes oficiais",
        body: "Existem milhares de sites que se passam pelo governo para vender serviços que são gratuitos. Use sempre os endereços terminados em .gov.br.",
      },
    ],
    faq: [
      {
        q: "Por que vocês não fazem tudo aqui?",
        a: "Operações oficiais (emitir DAS, declaração) precisam de autenticação gov.br e só podem ser feitas no site do governo.",
      },
    ],
  },

  /* ===== NOVAS FERRAMENTAS SEO ===== */
  "simulador-aposentadoria-mei": {
    h1: "Simulador de Aposentadoria MEI 2026",
    metaTitle: "Simulador Aposentadoria MEI 2026 Grátis: Calcule Online",
    metaDesc:
      "Simulador de aposentadoria MEI 2026 grátis: descubra em quanto tempo você se aposenta como microempreendedor pelo INSS e o valor estimado do benefício.",
    intro:
      "Descubra em quantos anos você pode se aposentar como MEI e qual será o valor do benefício pago pelo INSS.",
    Tool: SimuladorAposentadoriaMEI,
    sections: [
      {
        h2: "Como funciona a aposentadoria do MEI",
        body: "O MEI contribui ao INSS através do DAS mensal — 5% do salário mínimo. Essa contribuição garante o direito à aposentadoria por idade (65 anos para homens e 62 anos para mulheres), desde que cumpridos 180 meses de carência (15 anos). O valor do benefício corresponde a 1 salário mínimo vigente na data da concessão.",
      },
      {
        h2: "Aposentadoria por tempo de contribuição",
        body: "Para se aposentar por tempo de contribuição (regra antiga, válida para quem já contribuía antes de 2019, ou nas regras de transição), o MEI precisa complementar o INSS pagando uma guia GPS adicional de 15% do salário mínimo, totalizando 20%. Isso muda completamente o cálculo do benefício, que pode ser bem maior que 1 SM.",
      },
      {
        h2: "O que conta como tempo de contribuição",
        body: "Cada mês de DAS pago entra no extrato CNIS (Cadastro Nacional de Informações Sociais). Períodos como CLT, autônomo com guia GPS, e outras categorias também somam. Verifique seu extrato no Meu INSS antes de simular.",
      },
    ],
    faq: [
      {
        q: "MEI pode se aposentar por tempo de contribuição?",
        a: "Apenas se complementar o INSS pagando uma guia adicional de 15% (totalizando 20% do SM). Sem complemento, só tem direito à aposentadoria por idade no valor de 1 salário mínimo.",
      },
      {
        q: "Atraso no DAS atrasa a aposentadoria?",
        a: "Sim. Meses em atraso só passam a contar para carência depois de quitados. Acumular muitos meses pode fazer perder a qualidade de segurado e direito a auxílio-doença.",
      },
      {
        q: "Posso somar tempo de CLT com MEI?",
        a: "Sim. O CNIS consolida todas as suas contribuições. Tempo como CLT, autônomo e MEI somam para os 180 meses de carência exigidos.",
      },
    ],
  },
  "calculadora-inss-mei": {
    h1: "Calculadora INSS MEI 2026",
    metaTitle: "Calculadora INSS MEI 2026 Grátis: Valor da Contribuição",
    metaDesc:
      "Calculadora INSS MEI 2026 grátis: veja quanto do seu DAS vai para o INSS, como complementar contribuição e garantir aposentadoria por tempo de serviço.",
    intro:
      "Saiba exatamente quanto você contribui ao INSS como MEI, qual benefício isso garante e quando vale a pena complementar.",
    Tool: CalculadoraINSSMEI,
    sections: [
      {
        h2: "Quanto o MEI paga de INSS em 2026",
        body: "Em 2026, o MEI contribui com 5% do salário mínimo ao INSS — aproximadamente R$ 75,90 (sobre SM estimado de R$ 1.518). Esse valor já está embutido no DAS mensal, então não há guia separada para pagar.",
      },
      {
        h2: "Quais benefícios o INSS do MEI garante",
        body: "Com 12 meses de contribuição em dia o MEI tem direito a auxílio-doença, salário-maternidade e pensão por morte. Com 180 meses (15 anos), ganha direito à aposentadoria por idade. Auxílio-acidente e aposentadoria por invalidez também são cobertos.",
      },
      {
        h2: "Quando vale a pena complementar para 20%",
        body: "Se você quer se aposentar por tempo de contribuição (com valor superior a 1 SM), precisa complementar pagando uma guia GPS de 15% adicional — totalizando 20%. Vale para quem já tem renda alta hoje e quer manter padrão na aposentadoria.",
      },
    ],
    faq: [
      {
        q: "O INSS do MEI dá direito a aposentadoria?",
        a: "Sim. Aposentadoria por idade, no valor de 1 salário mínimo, após 180 meses de contribuição e idade mínima (65 H / 62 M).",
      },
      {
        q: "Posso pagar GPS para complementar?",
        a: "Sim. A guia GPS adicional de 15% do SM permite habilitar aposentadoria por tempo de contribuição e benefícios proporcionais à média salarial.",
      },
      {
        q: "Atrasar o DAS perde o INSS?",
        a: "O mês em atraso não conta enquanto não for pago. Após a quitação, ele entra na contagem normalmente — mas a qualidade de segurado tem período de manutenção limitado.",
      },
    ],
  },
  "simulador-das-atraso": {
    h1: "Simulador DAS em Atraso",
    metaTitle: "Simulador DAS em Atraso 2026: Total com Multa e Juros",
    metaDesc:
      "Simulador DAS MEI em atraso 2026: calcule o total a pagar com multa, juros Selic e veja opções de parcelamento direto no Portal do Empreendedor agora.",
    intro:
      "Acumulou várias guias DAS atrasadas? Veja o total atualizado com multa, juros e simule o parcelamento em até 60 vezes.",
    Tool: SimuladorDASAtraso,
    sections: [
      {
        h2: "Quanto custa o DAS atrasado",
        body: "Cada guia em atraso recebe multa de 0,33% por dia (limitada a 20% do valor) somada aos juros pela taxa Selic acumulada. Em 6 meses de atraso, o valor pode quase dobrar quando somados todos os encargos. Quitar à vista evita o efeito cumulativo de juros sobre juros.",
      },
      {
        h2: "Parcelamento em até 60 vezes",
        body: "O Portal do Empreendedor permite parcelar o DAS atrasado em até 60 parcelas, com valor mínimo de R$ 50 por parcela. O parcelamento é feito direto no PGMEI e suspende a inscrição em dívida ativa enquanto as parcelas estão em dia.",
      },
      {
        h2: "Riscos de manter DAS em aberto",
        body: "Além dos juros crescentes, o CNPJ pode ser cancelado por inadimplência prolongada, ir para dívida ativa, gerar bloqueio bancário e impedir emissão de certidões. Você também perde meses para fins de aposentadoria enquanto não regulariza.",
      },
    ],
    faq: [
      {
        q: "Posso pagar só algumas guias e deixar outras?",
        a: "Sim, o PGMEI permite emitir guia atualizada de cada competência individualmente. Priorize as mais antigas para evitar o teto de 20% de multa em todas.",
      },
      {
        q: "Parcelamento conta como contribuição ao INSS?",
        a: "O INSS de cada mês só passa a contar após o pagamento integral daquela competência (incluindo as parcelas referentes a ela).",
      },
      {
        q: "Posso reparcelar se atrasar?",
        a: "Sim, é possível pedir reparcelamento, mas há limite de tentativas. O ideal é configurar débito automático para evitar nova inadimplência.",
      },
    ],
  },
  "gerador-orcamento-pdf": {
    h1: "Gerador de Orçamento em PDF para MEI",
    metaTitle: "Gerador de Orçamento PDF Grátis: Profissional em 1 Minuto",
    metaDesc:
      "Gerador de orçamento em PDF grátis para MEI: monte propostas profissionais com seus dados, itens, valores e prazos prontos para enviar ao cliente hoje.",
    intro:
      "Crie em minutos um orçamento profissional em PDF para enviar ao cliente — com seus dados, itens, valores e validade da proposta.",
    Tool: GeradorOrcamentoPDF,
    sections: [
      {
        h2: "Por que enviar orçamento em PDF",
        body: "Mensagens soltas no WhatsApp ou no e-mail passam impressão amadora. Um PDF profissional, com seus dados, CNPJ, itens detalhados e validade da proposta, aumenta significativamente a taxa de fechamento e protege ambos os lados em caso de discordância sobre escopo ou preço.",
      },
      {
        h2: "O que um bom orçamento deve conter",
        body: "Nome e CNPJ do prestador, nome do cliente, data de emissão, lista clara de itens com quantidade e preço unitário, valor total destacado, prazo de validade da proposta, formas de pagamento aceitas e observações (prazo de execução, garantias). Esses elementos eliminam dúvidas e antecipam o fechamento.",
      },
      {
        h2: "Validade da proposta",
        body: "Estabeleça uma validade clara (7, 15 ou 30 dias). Isso cria senso de urgência saudável, protege você de variações de custo e demonstra organização. Após a validade, valores e condições podem ser renegociados.",
      },
    ],
    faq: [
      {
        q: "O PDF gerado fica salvo em algum servidor?",
        a: "Não. O PDF é gerado direto no seu navegador e baixado para o seu dispositivo. Nenhum dado é enviado para servidores nossos.",
      },
      {
        q: "Posso usar logo da minha empresa?",
        a: "Esta versão gera PDF clean com seu nome e CNPJ. Para incluir logo personalizada, use o Gerador de Recibo (versão Pro) que suporta marca.",
      },
      {
        q: "Orçamento tem validade jurídica?",
        a: "O orçamento é uma proposta comercial. Vira documento legal ao ser aceito por escrito (e-mail, WhatsApp, assinatura). Mantenha sempre o registro do aceite do cliente.",
      },
    ],
  },
  "comparador-conta-pj": {
    h1: "Comparador de Conta PJ 2026",
    metaTitle: "Comparador de Conta PJ 2026: Melhor Conta MEI Grátis",
    metaDesc:
      "Comparador de conta PJ grátis: compare lado a lado Nubank, Inter, Mercado Pago, C6 e PagBank — taxas, rendimento do CDI, boleto, cartão e app em 2026.",
    intro:
      "Compare as melhores contas digitais PJ para MEI em segundos: mensalidade, rendimento do saldo, Pix, boleto e cartão lado a lado.",
    Tool: ComparadorContaPJ,
    sections: [
      {
        h2: "Por que ter uma conta PJ separada do CPF",
        body: "Misturar a conta pessoal com a do negócio é um dos erros mais comuns do MEI. Uma conta PJ separada dá controle real do faturamento, facilita a DASN-SIMEI, profissionaliza a relação com clientes e fornecedores e cria histórico financeiro para acessar crédito empresarial. Em 2026, a maioria dos bancos digitais abre a conta 100% online, em poucos minutos e sem mensalidade.",
      },
      {
        h2: "O que comparar antes de abrir",
        body: "Mensalidade (gratuita ou cobrada), rendimento automático do saldo (0% a 105% do CDI), Pix ilimitado, custo de emissão de boletos (grátis, com limite mensal ou cobrança por unidade), cartão PJ de débito/crédito com ou sem anuidade, integração com maquininhas e acesso a crédito. O comparador acima organiza esses pontos em cards e tabela.",
      },
      {
        h2: "Documentos para abrir conta PJ digital",
        body: "CNPJ ativo, documento de identidade (RG, CNH ou passaporte), CPF do titular, comprovante de endereço (para MEI pode ser o do titular) e selfie para validação biométrica. Nenhuma das principais fintechs exige visita presencial — tudo é feito pelo app ou pelo computador em 5 a 15 minutos.",
      },
    ],
    faq: [
      {
        q: "MEI é obrigado a ter conta PJ?",
        a: "Não. Não existe lei que obrigue o MEI a ter conta PJ. Mas separar finanças é essencial para a saúde do negócio, e várias contas digitais são 100% gratuitas — não há motivo para misturar com o CPF.",
      },
      {
        q: "Qual conta PJ rende mais em 2026?",
        a: "Entre as listadas, Mercado Pago PJ rende 105% do CDI no saldo, seguida por Banco Inter PJ e PagBank PJ com 100% do CDI. Nubank PJ e C6 Bank PJ não rendem na conta corrente.",
      },
      {
        q: "Posso ter mais de uma conta PJ?",
        a: "Sim. Muitos MEIs usam uma conta para receber e outra com bom rendimento para reserva. Não há limite legal — só fique atento ao controle financeiro.",
      },
    ],
  },
  "simulador-emprestimo-mei": {
    h1: "Simulador de Empréstimo MEI 2026",
    metaTitle: "Simulador de Empréstimo MEI 2026: Parcelas, Juros e CET",
    metaDesc:
      "Simulador de empréstimo MEI 2026 grátis: calcule parcela, custo total, juros e CET pelo sistema Price e compare Pronampe, fintechs e bancos lado a lado.",
    intro:
      "Simule o seu empréstimo MEI em segundos: parcela, CET anual e comparativo das principais linhas de crédito para microempreendedor em 2026.",
    Tool: SimuladorEmprestimoMEI,
    sections: [
      {
        h2: "Como o simulador calcula a parcela",
        body: "O cálculo segue a Tabela Price (sistema francês de amortização), o mesmo modelo adotado por bancos e fintechs no Brasil. As parcelas são fixas ao longo de todo o contrato: no início, a maior parte de cada parcela é juros e uma fração menor amortiza o saldo devedor; com o passar do tempo, essa relação se inverte e cada parcela paga mais do principal.",
      },
      {
        h2: "Pronampe x fintech x banco tradicional",
        body: "Em 2026, o Pronampe é a linha mais barata para o MEI (a partir de cerca de 1,5% ao mês, equivalente a Selic + 6% a.a.) — exige documentação em ordem e CNPJ ativo há mais de 1 ano. Fintechs como Nubank PJ, PagBank e Mercado Crédito aprovam em minutos, mas cobram entre 3,5% e 4,5% ao mês. Bancos tradicionais (Itaú, BB, Caixa, Bradesco) ficam no meio: taxas a partir de 2% a.m. quando há bom relacionamento e movimentação relevante na conta PJ.",
      },
      {
        h2: "Capacidade de pagamento: a regra dos 30%",
        body: "Bancos analisam crédito com base no lucro líquido mensal, não no faturamento. A recomendação prática é que a parcela do empréstimo não passe de 30% do lucro mensal — assim sobra caixa para giro, imprevistos e o próprio DAS. Se a parcela simulada ultrapassar esse limite, considere reduzir o valor solicitado ou aumentar o prazo (com o custo extra de juros).",
      },
    ],
    faq: [
      {
        q: "O CET mostrado no simulador é o oficial?",
        a: "Não. O simulador estima o CET anual a partir da taxa mensal informada. O CET oficial é divulgado pela instituição na proposta formal e inclui IOF, tarifas, seguros e demais encargos.",
      },
      {
        q: "Posso simular sem ter CNPJ ativo?",
        a: "Sim. A simulação é totalmente anônima e não exige cadastro. Mas para contratar de verdade você precisa do CNPJ ativo, geralmente há pelo menos 6 a 12 meses.",
      },
      {
        q: "Vale mais a pena prazo curto ou longo?",
        a: "Prazo curto reduz drasticamente os juros totais, mas exige parcela maior. Prazo longo dilui o impacto mensal, mas pode dobrar o custo total. Use o simulador para encontrar o ponto que cabe no seu caixa.",
      },
    ],
  },
  "simulador-maquininha": {
    h1: "Simulador de Taxas de Maquininhas para MEI",
    metaTitle:
      "Simulador de Maquininha 2026: Compare Taxas Grátis",
    metaDesc:
      "Compare gratuitamente as taxas das principais maquininhas do Brasil. Descubra qual deixa mais dinheiro no seu bolso antes de comprar.",
    intro:
      "Compare Stone, InfinitePay, Mercado Pago, Ton, PagBank e SumUp em poucos segundos e descubra qual oferece o melhor custo-benefício para o seu negócio.",
    Tool: SimuladorMaquininha,
    sections: [
      {
        h2: "Como o simulador calcula o valor líquido",
        body: "A ferramenta aplica a taxa de cada maquininha sobre o valor da venda informado e mostra o valor líquido recebido (venda × (1 − taxa%)). Em seguida, ordena as operadoras da menor para a maior taxa e projeta a diferença mensal e anual com base no seu faturamento estimado — assim você visualiza o impacto real de cada decimal de taxa no seu caixa.",
      },
      {
        h2: "Maquininha sem aluguel x com aluguel",
        body: "Ton, SumUp, PagBank, Mercado Pago e InfinitePay vendem o aparelho e cobram só a taxa por transação — modelo certo para MEI com faturamento até R$ 30 mil/mês. Cielo, Rede e Getnet podem oferecer taxas menores em troca de mensalidade fixa, compensando apenas para faturamentos acima de R$ 50 mil/mês. Use o <a href='#ferramenta'>simulador</a> para encontrar o ponto de equilíbrio.",
      },
      {
        h2: "Prazo de recebimento impacta o fluxo de caixa",
        body: "Receber em D+0 (no mesmo dia) ou D+1 (próximo dia útil) garante capital de giro para repor estoque e pagar fornecedores sem precisar antecipar recebíveis. Receber em D+30 (padrão do crédito sem antecipação) atrasa o caixa, mas elimina o custo da antecipação. Em 2026, InfinitePay e Mercado Pago se destacam pelo recebimento rápido sem taxa adicional.",
      },
      {
        h2: "Quando vale a pena trocar de maquininha?",
        body: "Vale trocar de maquininha quando: (1) sua taxa atual está acima de 1% no débito ou acima de 3% no crédito à vista; (2) você perde vendas por indisponibilidade técnica; (3) o prazo de recebimento compromete seu capital de giro; (4) uma concorrente oferece taxa menor sem aumentar o prazo de recebimento. Use o simulador de maquininha para calcular a economia exata antes de migrar.",
      },
    ],
    faq: [
      {
        q: "Qual é a melhor maquininha para MEI em 2026?",
        a: "Depende do seu perfil de vendas. Para menor taxa no débito: PagBank (0,58%). Para menor taxa no crédito parcelado 12x: Ton (3,94%). Para recebimento na hora: InfinitePay e Mercado Pago. Para melhor suporte: Stone. Use o simulador acima para comparar com base no seu faturamento e ticket médio real.",
      },
      {
        q: "Como funciona o simulador de maquininha?",
        a: "Você informa o valor da venda, a modalidade de pagamento (débito, crédito à vista ou parcelado) e o faturamento mensal estimado. O simulador calcula o valor líquido recebido em cada maquininha, ordena do mais vantajoso para o menos vantajoso e projeta a economia mensal e anual. Gratuito, sem cadastro, resultado instantâneo.",
      },
      {
        q: "Qual maquininha possui a menor taxa?",
        a: "No débito e crédito à vista, o PagBank lidera com 0,58%. No crédito parcelado 12x, o Ton tem 3,94% — a menor do mercado. O Pix é taxa zero em praticamente todas as maquininhas.",
      },
      {
        q: "InfinitePay é melhor que Mercado Pago?",
        a: "Depende do uso. A InfinitePay tem taxa de débito de 1,09% com recebimento em D+1, enquanto o Mercado Pago tem 1,49% no débito mas recebe em D+0. Se você prioriza velocidade de recebimento, o Mercado Pago pode ser melhor; se prioriza taxa mais baixa, a InfinitePay leva vantagem. Simule com seu perfil para a resposta definitiva.",
      },
      {
        q: "Stone vale a pena para MEI?",
        a: "A Stone é excelente para MEIs com faturamento médio-alto que valorizam suporte de qualidade e taxas competitivas no crédito parcelado. Recebe em D+1 sem mensalidade. É reconhecida como a maquininha com melhor suporte ao cliente do Brasil.",
      },
      {
        q: "Ton tem aluguel?",
        a: "Não. A Ton (do Grupo Stone) não cobra mensalidade ou aluguel. Você paga apenas pelo aparelho na compra e pelas taxas de cada transação. Não há fidelidade contratual.",
      },
      {
        q: "Mercado Pago cobra mensalidade?",
        a: "Não. O Mercado Pago Point não cobra mensalidade. Você adquire o aparelho e paga apenas as taxas por transação. O recebimento é na hora (D+0) no débito e em D+14 no crédito.",
      },
      {
        q: "Como calcular as taxas da maquininha?",
        a: "Para calcular o valor líquido de uma venda: multiplique o valor da venda pela taxa percentual e subtraia o resultado. Exemplo: venda de R$ 200 com taxa de 2% = R$ 200 × 0,02 = R$ 4 de taxa → você recebe R$ 196 líquido. Use o simulador acima para calcular automaticamente para todas as maquininhas ao mesmo tempo.",
      },
      {
        q: "As taxas exibidas no simulador são oficiais?",
        a: "São referências de mercado em jun/2026 para os planos mais populares de cada operadora. As taxas reais podem variar conforme plano, fidelidade, volume de faturamento e perfil do MEI. Confirme sempre na proposta oficial antes de contratar.",
      },
      {
        q: "Por que o Pix tem taxa zero na maquininha?",
        a: "A maioria das maquininhas não cobra taxa pelo Pix porque a liquidação é feita pelo SPI (Sistema de Pagamentos Instantâneos) do Banco Central — o custo operacional é praticamente nulo. Migrar vendas do crédito para o Pix economiza até 4,60% por transação.",
      },
      {
        q: "Posso usar mais de uma maquininha ao mesmo tempo?",
        a: "Sim. Muitos MEIs usam duas operadoras para aproveitar o melhor de cada — por exemplo, uma com menor taxa de débito e outra com melhor prazo no crédito parcelado. Compare os cenários no simulador.",
      },
      {
        q: "Vale antecipar recebíveis na maquininha?",
        a: "Maquininhas cobram entre 1% e 4% para antecipar parcelas de crédito. Antes de antecipar, compare com o custo de uma linha de capital de giro no banco — pode sair mais barato e preservar a sua margem.",
      },
      {
        q: "PagBank tem boa taxa de crédito parcelado?",
        a: "Não. Embora o PagBank tenha a menor taxa no débito (0,58%), sua taxa no crédito parcelado 12x (7,98%) é a mais alta entre as principais maquininhas. Se você vende muito no parcelado, Stone ou Ton são opções mais competitivas.",
      },
      {
        q: "SumUp é boa opção para MEI?",
        a: "A SumUp é indicada para MEIs com baixo volume de vendas ou que precisam de uma maquininha de backup. Suas taxas são mais altas que concorrentes (1,45% no débito), mas o aparelho é compacto e o custo de entrada é acessível.",
      },
      {
        q: "Qual maquininha recebe na hora em 2026?",
        a: "Em 2026, as maquininhas que oferecem recebimento imediato (D+0) no débito são: Mercado Pago Point. InfinitePay oferece D+1 mesmo no crédito parcelado. PagBank também oferece recebimento rápido dependendo do plano.",
      },
    ],
  },
  "mei-vs-clt-2026": {
    h1: "Simulador MEI vs CLT 2026",
    metaTitle: "MEI ou CLT 2026? Simulador Grátis Compara Líquido",
    metaDesc:
      "Simulador MEI vs CLT 2026 grátis: compare salário líquido nos dois regimes com INSS progressivo, IRPF atualizado (Lei 15.270/2025) e DAS-MEI vigente.",
    intro:
      "Descubra em segundos qual regime entrega mais dinheiro líquido no seu bolso — com as tabelas de INSS, IRPF (Reforma da Renda) e DAS-MEI atualizadas para 2026.",
    Tool: SimuladorMeiVsClt,
    sections: [
      {
        h2: "Como o simulador calcula o líquido",
        body: "Para o CLT, aplicamos a tabela progressiva oficial do INSS 2026 (7,5% a 14%, com teto em R$ 988,09) e a tabela do IRPF com a isenção da Lei 15.270/2025 — sem imposto até R$ 5.000/mês e redução parcial até R$ 7.350. Acrescentamos 13º e férias rateados mensalmente para representar o valor real recebido. Para o MEI, descontamos o DAS conforme a atividade (R$ 82,05 a R$ 87,05) e aplicamos a isenção legal sobre o lucro (8% para comércio, 32% para serviços, 20% para misto) antes de calcular IRPF sobre o lucro tributável.",
      },
      {
        h2: "O que muda em 2026",
        body: "A grande novidade é a isenção total do Imposto de Renda para rendas até R$ 5.000/mês e a redução parcial até R$ 7.350, aprovada pela Lei nº 15.270/2025. Isso aumentou o líquido do CLT na base da pirâmide salarial e mudou o ponto de equilíbrio na comparação com o MEI. O DAS-MEI também foi reajustado pelo novo salário mínimo de R$ 1.621, ficando entre R$ 82,05 (comércio) e R$ 87,05 (misto).",
      },
      {
        h2: "Cuidados com a pejotização",
        body: "Receber uma proposta para virar PJ/MEI exige análise. Se o trabalho tiver subordinação, pessoalidade, habitualidade e onerosidade, é vínculo empregatício — não importa o nome do contrato. Pejotização irregular gera passivo trabalhista para a empresa e insegurança jurídica para você. Use o simulador como ponto de partida, mas leve a proposta a um contador ou advogado antes de assinar.",
      },
    ],
    faq: [
      {
        q: "O simulador considera a Reforma do IR de 2026?",
        a: "Sim. Aplicamos a Lei 15.270/2025: isenção total até R$ 5.000/mês e redução parcial linear entre R$ 5.000 e R$ 7.350. Acima desse valor, vale a tabela progressiva normal.",
      },
      {
        q: "O líquido CLT inclui FGTS?",
        a: "O FGTS (8%) não vai para a sua conta corrente todo mês, mas embutimos 13º e férias + 1/3 rateados mensalmente para mostrar o valor real recebido ao longo do ano. O FGTS é uma reserva adicional acessada em desligamento, aposentadoria ou doenças graves.",
      },
      {
        q: "Existe risco em virar MEI sendo CLT hoje?",
        a: "Sim. Você perde FGTS, seguro-desemprego, férias remuneradas e aviso prévio. Em contrapartida, ganha autonomia e pode (em algumas faixas de renda) líquido maior. Pondere também o teto de R$ 81 mil/ano do MEI: ultrapassar gera desenquadramento e migração obrigatória para ME.",
      },
    ],
  },
  "ia-cnae-mei-2026": {
    h1: "IA CNAE MEI 2026 — Descubra o CNAE Ideal para sua Atividade",
    metaTitle: "IA CNAE MEI 2026: Descubra Gratuitamente o CNAE Ideal",
    metaDesc:
      "Descubra gratuitamente qual CNAE escolher para seu MEI. Consulte atividades permitidas, DAS estimado, riscos, vantagens e CNAEs relacionados com IA.",
    intro:
      "Descreva o que você faz em linguagem natural e a IA encontra o CNAE certo para o seu MEI — com DAS estimado, vantagens, riscos e atividades relacionadas.",
    Tool: IaCnaeMei,
    sections: [
      {
        h2: "Por que o CNAE certo é fundamental para o MEI",
        body: "O CNAE define o valor do DAS, a possibilidade de emitir nota fiscal, os tipos de cliente que você pode atender e até se o seu negócio pode continuar como MEI. Em 2026, são 467 atividades permitidas — e mais de 800 vedadas. Errar o código no Portal do Empreendedor é uma das principais causas de desenquadramento involuntário e de notas rejeitadas em marketplaces como Mercado Livre e Shopee.",
      },
      {
        h2: "Como a IA encontra o código certo",
        body: "Em vez de obrigar você a decorar termos da tabela CNAE, a ferramenta interpreta sua descrição em linguagem natural — 'faço bolo por encomenda', 'instalo ar-condicionado', 'revendo roupas pelo Instagram' — e cruza com a lista oficial atualizada de 2026. Para cada resultado, mostra se é permitido no MEI, o DAS estimado (R$ 71,60 a R$ 76,60), as principais vantagens, os pontos de atenção e atividades correlatas que você pode incluir como secundárias (até 15).",
      },
      {
        h2: "Atividade vedada? A ferramenta indica o caminho",
        body: "Quando a atividade descrita não está na lista do MEI (medicina, engenharia, advocacia, consultoria de TI, importação direta), a ferramenta aponta o motivo e sugere alternativas — geralmente abertura de Microempresa (ME) no Simples Nacional, com limite de R$ 360 mil/ano. Isso evita que você abra um MEI 'no escuro' e descubra meses depois que precisa migrar.",
      },
    ],
    faq: [
      {
        q: "Quantos CNAEs um MEI pode ter?",
        a: "Cada MEI pode ter até 16 CNAEs: 1 principal e até 15 secundários. Todos precisam estar na lista oficial de atividades permitidas no MEI — basta um vedado para a inscrição ou alteração ser bloqueada.",
      },
      {
        q: "Qual o limite de faturamento do MEI em 2026?",
        a: "O limite de faturamento anual do MEI em 2026 segue em R$ 81.000 (cerca de R$ 6.750/mês). Ao ultrapassar, o microempreendedor é desenquadrado automaticamente e passa a recolher tributos como Microempresa (ME) no Simples Nacional.",
      },
      {
        q: "Quantas atividades são permitidas no MEI?",
        a: "São mais de 467 atividades permitidas no MEI em 2026, divididas entre comércio, indústria e serviços não regulamentados. A lista é atualizada pelo CGSN e qualquer atividade fora dela é considerada vedada.",
      },
      {
        q: "Posso alterar meu CNAE depois de abrir o MEI?",
        a: "Sim. A alteração de CNAE é gratuita, feita em poucos minutos no Portal do Empreendedor (gov.br/mei), e passa a valer no mês seguinte. Você pode trocar o CNAE principal, adicionar secundários (até 15) ou remover ocupações que não exerce mais.",
      },
      {
        q: "A ferramenta substitui um contador?",
        a: "Para a maioria dos casos simples (cabeleireiro, eletricista, designer, comércio de roupas, lanchonete), sim. Para atividades híbridas, mistas ou na borda da lista, sempre confirme com um profissional antes de abrir o CNPJ.",
      },
      {
        q: "Preciso pagar ou me cadastrar?",
        a: "Não. A ferramenta é 100% gratuita, sem cadastro, sem login e sem coleta de dados — tudo roda direto no seu navegador.",
      },
      {
        q: "Posso usar o resultado direto no Portal do Empreendedor?",
        a: "Sim. O código sugerido segue o padrão oficial da Receita Federal e pode ser informado diretamente no formulário de abertura ou de alteração do MEI no gov.br/empresas-e-negocios/mei.",
      },
    ],
  },
};

export const Route = createFileRoute("/ferramentas/$slug")({
  loader: async ({ params }) => {
    // Redirects 301 de slugs antigos para os novos (SEO-friendly URLs).
    if (params.slug === "ferramentas-mei-kit-gratuito") {
      throw redirect({ to: "/artigos/$slug", params: { slug: "ferramentas-mei-kit-gratuito" }, statusCode: 301 });
    }
    const SLUG_REDIRECTS: Record<string, string> = {
      "separador-despesas": "separador-despesas-mei",
    };
    const redirectTo = SLUG_REDIRECTS[params.slug];
    if (redirectTo) {
      throw redirect({ to: "/ferramentas/$slug", params: { slug: redirectTo }, statusCode: 301 });
    }
    const content = CONTENT[params.slug];
    if (!content) throw notFound();
    const { Tool, ...serializableContent } = content;

    // Avaliações reais (aprovadas) para o schema aggregateRating.
    let rating: { value: string; count: number } | null = null;
    try {
      const { data } = await supabase
        .from("ratings")
        .select("rating")
        .eq("content_type", "tool")
        .eq("content_slug", params.slug)
        .eq("status", "approved");
      if (data && data.length > 0) {
        const sum = data.reduce((s: number, r: { rating: number | null }) => s + (r.rating ?? 0), 0);
        rating = { value: (sum / data.length).toFixed(1), count: data.length };
      }
    } catch {
      rating = null;
    }

    return { slug: params.slug, content: serializableContent, rating };
  },

  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const c = loaderData.content;
    const ext = getSeoExtension(loaderData.slug);
    const url = `https://meitoolbox.com.br/ferramentas/${loaderData.slug}`;
    const metaTags: Array<Record<string, string>> = [
      { title: c.metaTitle },
      { name: "description", content: c.metaDesc },
      { property: "og:title", content: c.metaTitle },
      { property: "og:description", content: c.metaDesc },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "MEI Toolbox" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:image", content: "https://meitoolbox.com.br/og-default.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: c.metaTitle },
      { name: "twitter:description", content: c.metaDesc },
      { name: "twitter:image", content: "https://meitoolbox.com.br/og-default.png" },
    ];
    if (ext?.keywords?.length) {
      metaTags.push({ name: "keywords", content: ext.keywords.join(", ") });
    }
    return {
      meta: metaTags,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [...c.faq, ...(ext?.extraFaq ?? []), ...(c.paa ?? [])].map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: c.h1,
            description: c.metaDesc,
            url: `https://meitoolbox.com.br/ferramentas/${loaderData.slug}`,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            inLanguage: "pt-BR",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "BRL",
            },
            ...(loaderData.rating
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: loaderData.rating.value,
                    ratingCount: loaderData.rating.count,
                    bestRating: "5",
                    worstRating: "1",
                  },
                }
              : {}),

          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Início",
                item: "https://meitoolbox.com.br/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Ferramentas",
                item: "https://meitoolbox.com.br/ferramentas",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: c.h1,
                item: `https://meitoolbox.com.br/ferramentas/${loaderData.slug}`,
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: c.h1,
            description: c.metaDesc,
            url,
            inLanguage: "pt-BR",
            dateModified: "2026-06-04",
            isPartOf: {
              "@type": "WebSite",
              name: "MEI Toolbox",
              url: "https://meitoolbox.com.br/",
            },
          }),
        },
        ...(loaderData.slug === "calculadora-das-mei" ||
        loaderData.slug === "calculadora-multa-das" ||
        loaderData.slug === "limite-faturamento-mei" ||
        loaderData.slug === "simulador-irpf-mei" ||
        loaderData.slug === "simulador-maquininha"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  headline: c.h1,
                  description: c.metaDesc,
                  inLanguage: "pt-BR",
                  datePublished: "2026-01-15",
                  dateModified: "2026-06-28",
                  mainEntityOfPage: url,
                  author: {
                    "@type": "Person",
                    name: "Ismaque de Oliveira",
                    url: "https://meitoolbox.com.br/",
                  },
                  publisher: {
                    "@type": "Organization",
                    name: "MEI Toolbox",
                    url: "https://meitoolbox.com.br/",
                  },
                  about:
                    loaderData.slug === "calculadora-das-mei"
                      ? [
                          "DAS MEI 2026",
                          "valor DAS MEI",
                          "guia DAS MEI",
                          "boleto MEI",
                          "contribuição MEI",
                          "Simples Nacional",
                          "INSS MEI",
                        ]
                      : loaderData.slug === "calculadora-multa-das"
                        ? [
                            "DAS atrasado",
                            "multa do DAS",
                            "juros do DAS",
                            "parcelamento do MEI",
                            "dívida ativa MEI",
                            "regularizar MEI",
                          ]
                        : loaderData.slug === "limite-faturamento-mei"
                          ? [
                              "limite MEI 2026",
                              "teto MEI",
                              "faturamento MEI",
                              "desenquadramento MEI",
                              "migrar MEI para ME",
                              "receita bruta MEI",
                            ]
                          : loaderData.slug === "simulador-maquininha"
                            ? [
                                "melhor maquininha para MEI",
                                "simulador de taxas",
                                "comparador de maquininhas",
                                "taxa maquininha 2026",
                                "Stone",
                                "InfinitePay",
                                "Mercado Pago",
                                "Ton",
                                "PagBank",
                                "SumUp",
                              ]
                            : [
                                "IRPF MEI 2026",
                                "imposto de renda MEI",
                                "DASN-SIMEI",
                                "declaração MEI",
                                "renda tributável MEI",
                                "Receita Federal",
                              ],
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: ToolPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-24 text-center">
      <h1 className="font-display text-3xl font-bold">Ferramenta não encontrada</h1>
      <p className="mt-3 text-muted-foreground">Esta ferramenta ainda não foi lançada.</p>
      <Link
        to="/ferramentas"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-primary-foreground font-semibold"
      >
        Ver todas as ferramentas
      </Link>
    </div>
  ),
});

function ToolPage() {
  const { slug, content } = Route.useLoaderData();
  const Tool = CONTENT[slug]?.Tool;
  const ext = getSeoExtension(slug);
  const idx = tools.findIndex((t) => t.slug === slug);
  const next = tools.slice(idx + 1).find((t) => t.available);

  const allSections = [...content.sections, ...(ext?.extraSections ?? [])];
  const allFaq = [...content.faq, ...(ext?.extraFaq ?? [])];
  const ctaLabel = ext?.cta ?? "Usar a ferramenta";

  return (
    <article>
      {/* HERO */}
      <header className="bg-surface/50 border-b border-border">
        <div className="container mx-auto px-4 py-10 md:py-14 max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-foreground transition">
                  Início
                </Link>
              </li>
              <li aria-hidden="true" className="text-muted-foreground/60">
                ›
              </li>
              <li>
                <Link to="/ferramentas" className="hover:text-foreground transition">
                  Ferramentas
                </Link>
              </li>
              <li aria-hidden="true" className="text-muted-foreground/60">
                ›
              </li>
              <li className="text-foreground font-medium line-clamp-1" aria-current="page">
                {content.h1}
              </li>
            </ol>
          </nav>
          <h1 className="font-display text-3xl md:text-5xl font-extrabold leading-tight text-balance">
            {content.h1}
          </h1>
          {(slug === "calculadora-das-mei" ||
            slug === "calculadora-multa-das" ||
            slug === "limite-faturamento-mei" ||
            slug === "simulador-irpf-mei") && (
            <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground font-semibold">
              Última atualização: 04 de junho de 2026
            </p>
          )}

          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{content.intro}</p>

          {ext?.lead && (
            <a
              href="#ferramenta"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground font-semibold shadow-soft hover:opacity-90 transition"
            >
              <Sparkles className="h-4 w-4" /> {ctaLabel}
            </a>
          )}
        </div>
      </header>

      {slug === "calculadora-das-mei" && (
        <>
          <nav aria-label="Nesta página" className="container mx-auto px-4 max-w-3xl pt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-3">
              Nesta página:
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "O que é DAS-MEI", href: "#o-que-e-das-mei" },
                { label: "Quanto custa o DAS em 2026", href: "#quanto-custa-das-2026" },
                { label: "Como emitir e pagar", href: "#como-emitir-pagar" },
                { label: "Benefícios de pagar em dia", href: "#beneficios-pagar-em-dia" },
                { label: "Erros comuns", href: "#erros-comuns" },
                { label: "Perguntas frequentes", href: "#perguntas-frequentes" },
              ].map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="inline-flex items-center rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                >
                  {item.label}
                </a>
              ))}
            </div>
          </nav>
          <MeiQuickStats />
        </>
      )}

      {/* LEAD: long-form SEO intro (300+ palavras) */}
      {ext?.lead && (
        <section className="container mx-auto px-4 pt-10 max-w-3xl">
          <p className="text-base md:text-lg leading-relaxed text-foreground/90 whitespace-pre-line">
            {ext.lead}
          </p>
        </section>
      )}

      {slug === "simulador-maquininha" && (
        <Suspense fallback={null}>
          <SimuladorMaquinhaSEO />
        </Suspense>
      )}

      {slug === "simulador-maquininha" && (
        <section className="container mx-auto px-4 max-w-3xl mt-8">
          <InfinitePayBanner location="tool_intro" />
        </section>
      )}

      {/* TOOL */}
      <section id="ferramenta" className="container mx-auto px-4 max-w-5xl scroll-mt-20">
        <div className="mt-10">
          <Suspense fallback={
            <div className="h-64 flex flex-col items-center justify-center rounded-3xl border border-border bg-muted/20 animate-pulse">
              <span className="text-sm text-muted-foreground font-medium">Carregando ferramenta...</span>
            </div>
          }>
            <Tool />
          </Suspense>
        </div>
        {slug === "simulador-maquininha" && <InfinitePayBanner location="tool_result" />}
        <div className="mt-8">
          <RatingStars contentType="tool" slug={slug} />
        </div>
      </section>

      {slug === "ia-cnae-mei-2026" && (
        <Suspense fallback={null}>
          <CnaeGuiaSEO />
          <section className="container mx-auto px-4 pt-6 max-w-3xl">
            <AuthorBlock />
          </section>
        </Suspense>
      )}

      {slug === "calculadora-das-mei" && (
        <Suspense fallback={null}>
          <DasMeiSEO />
        </Suspense>
      )}

      {slug === "calculadora-multa-das" && (
        <Suspense fallback={null}>
          <MultaDasExemplos />
          <MultaDasCronograma />
          <MultaDasQuandoParcelar />
          <MultaDasErros />
          <MultaDasLinks />
          <section className="container mx-auto px-4 max-w-3xl mt-12">
            <AuthorBlock />
          </section>
        </Suspense>
      )}

      {slug === "limite-faturamento-mei" && (
        <Suspense fallback={null}>
          <LimiteMeiSEO />
          <section className="container mx-auto px-4 max-w-3xl mt-12">
            <p className="text-xs text-muted-foreground italic text-center">
              Conteúdo revisado com base nas regras vigentes do Simples Nacional e do Portal do
              Empreendedor.
            </p>
          </section>
          <section className="container mx-auto px-4 max-w-3xl mt-6">
            <AuthorBlock />
          </section>
        </Suspense>
      )}

      {slug === "simulador-irpf-mei" && (
        <Suspense fallback={null}>
          <IrpfMeiSEO />
          <section className="container mx-auto px-4 max-w-3xl mt-12">
            <p className="text-xs text-muted-foreground italic text-center">
              Conteúdo revisado com base nas regras vigentes da Receita Federal para Pessoa Física e
              Microempreendedor Individual.
            </p>
          </section>
          <section className="container mx-auto px-4 max-w-3xl mt-6">
            <AuthorBlock />
          </section>
        </Suspense>
      )}

      {/* CONTENT */}
      <section className="container mx-auto px-4 py-16 max-w-3xl space-y-10">
        {allSections.map((s: { h2: string; body: string }, i: number) => {
          const sectionAnchors: Record<string, string> = {
            "O que é o DAS MEI?": "o-que-e-das-mei",
            "Quanto custa o DAS MEI em 2026?": "quanto-custa-das-2026",
            "O que acontece se eu não pagar o DAS?": "o-que-acontece-se-nao-pagar",
            "Como emitir a guia DAS MEI (passo a passo)": "como-emitir-pagar",
          };
          const sectionId = slug === "calculadora-das-mei" ? sectionAnchors[s.h2] : undefined;
          return (
            <div key={i} id={sectionId}>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">{s.h2}</h2>
              <p
                className="text-muted-foreground leading-relaxed text-lg [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:opacity-80"
                dangerouslySetInnerHTML={{ __html: s.body }}
              />
            </div>
          );
        })}

        {/* CTA mid-page — usa o CTA exclusivo da ferramenta */}
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-8 text-center">
          <h2 className="font-display text-xl md:text-2xl font-bold mb-2">{ctaLabel}</h2>
          <p className="text-muted-foreground mb-5">
            100% grátis, sem cadastro e atualizada para 2026 — feita para o microempreendedor
            brasileiro.
          </p>
          <a
            href="#ferramenta"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-primary-foreground font-semibold shadow-soft hover:opacity-90 transition"
          >
            <Sparkles className="h-4 w-4" /> {ctaLabel}
          </a>
        </div>

        {slug === "calculadora-das-mei" && (
          <Suspense fallback={null}>
            <DasMeiBeneficios />
            <DasMeiErros />
            <PeopleAlsoAsk />
          </Suspense>
        )}

        <div id="perguntas-frequentes">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">Perguntas frequentes</h2>
          <div className="space-y-3">
            {allFaq.map((f: { q: string; a: string }, i: number) => (
              <details
                key={i}
                className="group rounded-2xl border border-border bg-card p-5 shadow-soft"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between font-semibold">
                  <span>{f.q}</span>
                  <span className="ml-4 flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {slug === "simulador-maquininha" && <InfinitePayBanner location="tool_bottom" />}

        {slug === "calculadora-das-mei" && (
          <Suspense fallback={null}>
            <RelatedToolsDasMei />

            {/* CTA Final de Conversão */}
            <div className="rounded-3xl bg-gradient-hero p-8 md:p-10 text-white shadow-elevated text-center">
              <h2 className="font-display text-2xl md:text-3xl font-bold">
                Descubra agora o valor exato do seu DAS
              </h2>
              <p className="mt-3 text-white/85 max-w-2xl mx-auto">
                Evite erros e saiba exatamente quanto pagar para manter seu MEI regularizado.
              </p>
              <a
                href="#ferramenta"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-primary font-semibold hover:opacity-90 transition"
              >
                <Sparkles className="h-4 w-4" /> Calcular Meu DAS Gratuitamente
              </a>
            </div>

            <p className="text-xs text-muted-foreground italic text-center">
              Conteúdo revisado com base nas regras vigentes do Simples Nacional, Portal do
              Empreendedor e Receita Federal.
            </p>

            <DasMeiAutoridade />
          </Suspense>
        )}

        <RelatedTools
          slugs={getRelatedToolsForTool(slug, 4)}
          heading="Ferramentas que podem ajudar você"
        />

        <ToolCtaIA currentSlug={slug} />

        <RelatedArticles
          items={getRelatedArticlesForTool(slug, 4)}
          heading="Leituras recomendadas"
        />

        <ShareButtons url={`https://meitoolbox.com.br/ferramentas/${slug}`} title={content.h1} />

        {next && (
          <div className="rounded-2xl border-2 border-dashed border-border p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-primary">
                Próxima ferramenta
              </div>
              <div className="font-display text-lg font-bold mt-1">{next.title}</div>
            </div>
            <Link
              to="/ferramentas/$slug"
              params={{ slug: next.slug }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-primary-foreground font-semibold hover:opacity-90 transition"
            >
              Abrir <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </section>
    </article>
  );
}
